/**
 * "Approve for client" on safety register entries (MB's decision 1023, the Q895 follow-up B, 4 Nov 2026; R220;
 * docs/specs/safety-register.md §10; Postgres `20261104230000_register_show_client.sql`,
 * `20261104238000_client_safety_register.sql`). Every kind of record (accidents, incidents, near misses, landholder
 * complaints, and the two height breach kinds of decision 1021) can be approved for the project's client, as the safety
 * documents are shown (decision 926): tenant admins and managers choose; the client reads them on the client portal's
 * Safety register page (Preview as client shows the same); nothing is shown until someone chooses; "Withdraw from
 * client" undoes it. R220 (MB, 4 Nov 2026): "approve each safety event to be seen by the client" is this choice, named
 * so (before R220, "Show to client" and "Hide from client").
 *
 * The choice is Teslyn Cloud's (`safety_register_client`, keyed by the record's source table and id, so a height
 * breach is chosen against its breach report entry). Decision 1035 replaced 1023's client-safe field list: the client
 * reads the whole of an approved record, every field the staff details show (`registerDetailLines`), through
 * `my_safety_register_all` (and `my_safety_register` for one project), never the tables. Decision 1037: the client has
 * no "Print the form" (an internal form).
 *
 * R233 (MB, 5 Nov 2026): Teslyn Field has the same choice, online only, against Teslyn Cloud with the person's sign-in
 * (`registerClientOffer`). Nothing is queued: sharing with a client is a deliberate online step, and the client reads
 * the record from Teslyn Cloud, so a record that has not reached it yet cannot be approved.
 */
import type { HeightBreachEntry, HeightBreachFacts, HeightBreachKind } from './height-breach.js';
import { jspPrintedDate } from './jsp-meeting.js';
import {
  REGISTER_KIND_WORDS,
  breachWithdrawnText,
  registerRecordFromBreach,
  safetyActionWords,
  safetyOrder,
  safetyPhotoNames,
  type RegisterKind,
  type RegisterRecord,
  type SafetyEntry,
  type SafetyPhoto,
} from './safety-register.js';

/** Who chooses which records the client sees (decision 1023: as the safety documents, tenant admins and managers). */
export const REGISTER_CLIENT_ROLES = ['tenant_admin', 'manager'] as const;

export function canShowRegisterToClient(roles: readonly string[] | null): boolean {
  return (roles ?? []).some((r) => (REGISTER_CLIENT_ROLES as readonly string[]).includes(r));
}

/** The table a register record comes from: the key of its choice with its id. */
export type RegisterRecordSource = 'safety_entry' | 'height_breach';

export function registerSourceOf(r: Pick<RegisterRecord, 'from_breach'>): RegisterRecordSource {
  return r.from_breach ? 'height_breach' : 'safety_entry';
}

/** A `safety_register_client` row as staff read it. */
export interface RegisterClientChoice {
  readonly id: string;
  readonly project_id: string;
  readonly source: RegisterRecordSource;
  readonly record_id: string;
  readonly shown: boolean;
  readonly shown_at: string | null;
  readonly shown_by_name: string;
  readonly hidden_at: string | null;
  readonly hidden_by_name: string;
}

/** Whether a record can be shown now: live (not removed, not withdrawn from the breach report). */
export function registerShowable(r: Pick<RegisterRecord, 'deleted_at'>): boolean {
  return !r.deleted_at;
}

// ------------------------------------------------------------------------------------------------ R220: the words

/** The staff button, its undo and the list's chip (R220: "approve each safety event to be seen by the client"). */
export const REGISTER_APPROVE_WORDS = 'Approve for client';
export const REGISTER_WITHDRAW_WORDS = 'Withdraw from client';
export const REGISTER_APPROVED_WORDS = 'Approved for client';

/** Under the staff button (decision 1035: the client reads the whole entry). */
export const REGISTER_CLIENT_SEES =
  'The client reads the whole entry on the client portal’s Safety register page: every field of its details, the place on the map and the linked photos.';

/** The staff page's "Client" filter (R220): every record, those approved for the client, or those not approved. */
export const REGISTER_CLIENT_FILTER = ['all', 'approved', 'not_approved'] as const;
export type RegisterClientFilter = (typeof REGISTER_CLIENT_FILTER)[number];

export const REGISTER_CLIENT_FILTER_WORDS: Readonly<Record<RegisterClientFilter, string>> = {
  all: 'Approved or not',
  approved: 'Approved for client',
  not_approved: 'Not approved',
};

/** Whether a record passes the "Client" filter; a record nobody has chosen for is not approved. */
export function registerClientShown(
  r: { readonly client: Pick<RegisterClientChoice, 'shown'> | null },
  filter: RegisterClientFilter,
): boolean {
  if (filter === 'all') return true;
  const approved = r.client?.shown ?? false;
  return filter === 'approved' ? approved : !approved;
}

const printedDate = (iso: string | null | undefined): string => (iso ? jspPrintedDate(iso.slice(0, 10)) : '');

// ------------------------------------------------------------------- the step on offer, in Teslyn Cloud and Field

/** What pressing does: approve a record for the client, or withdraw it. */
export type RegisterClientStep = 'approve' | 'withdraw';

export const REGISTER_CLIENT_STEP_WORDS: Readonly<Record<RegisterClientStep, string>> = {
  approve: REGISTER_APPROVE_WORDS,
  withdraw: REGISTER_WITHDRAW_WORDS,
};

/**
 * The step on offer (decision 1023): tenant admins and managers only; a record already approved can always be
 * withdrawn, removed or not; only a live record can be approved.
 */
export function registerClientStep(
  roles: readonly string[] | null,
  r: Pick<RegisterRecord, 'deleted_at'>,
  choice: Pick<RegisterClientChoice, 'shown'> | null,
): RegisterClientStep | null {
  if (!canShowRegisterToClient(roles)) return null;
  if (choice?.shown) return 'withdraw';
  return registerShowable(r) ? 'approve' : null;
}

/** The notice after a step. */
export function registerClientDoneWords(topic: string, shown: boolean): string {
  return shown
    ? `“${topic}” is approved for the client: they read it on the client portal’s Safety register page.`
    : `“${topic}” is withdrawn from the client.`;
}

/**
 * What a laptop knows of Teslyn Cloud's choices (R233; they are never synced): `known`, the answer is in; `looking`,
 * asking; `offline`, no connection; `signed_out`, nobody signed in; `unavailable`, Teslyn Cloud did not answer.
 */
export type RegisterCloudReach = 'known' | 'looking' | 'offline' | 'signed_out' | 'unavailable';

const REGISTER_UNKNOWN_LINE: Readonly<Record<Exclude<RegisterCloudReach, 'known'>, string>> = {
  looking: 'Looking in Teslyn Cloud…',
  offline: 'Connect to see whether it is approved for the client',
  signed_out: 'Sign in to see whether it is approved for the client',
  unavailable: 'Teslyn Cloud did not say whether it is approved for the client',
};

/**
 * The "Client" line of a record's details: approved, with when and by whom, or not. Without Teslyn Cloud's answer a
 * laptop never says "not approved".
 */
export function registerClientLine(
  choice: Pick<RegisterClientChoice, 'shown' | 'shown_at' | 'shown_by_name'> | null,
  reach: RegisterCloudReach = 'known',
): string {
  if (reach !== 'known') return REGISTER_UNKNOWN_LINE[reach];
  return choice?.shown
    ? `Approved for the client ${[printedDate(choice.shown_at), choice.shown_by_name].filter(Boolean).join(' by ')}`
    : 'Not approved for the client';
}

/** R233: approving is an online step in Teslyn Field; it refuses rather than queues. */
export const REGISTER_CONNECT_WORDS: Readonly<Record<RegisterClientStep, string>> = {
  approve: 'Connect to approve for the client.',
  withdraw: 'Connect to withdraw from the client.',
};

export const REGISTER_SIGN_IN_WORDS: Readonly<Record<RegisterClientStep, string>> = {
  approve: 'Sign in to approve for the client.',
  withdraw: 'Sign in to withdraw from the client.',
};

/** R233: the client reads a record from Teslyn Cloud, so it must be there first. */
export const REGISTER_NOT_IN_CLOUD_WORDS =
  'This entry has not reached Teslyn Cloud yet, and the client reads it from there. It goes at this laptop’s next sync; approve it after that.';

export const REGISTER_CLOUD_QUIET_WORDS =
  'Teslyn Cloud did not answer, so this cannot be done now. Try again soon.';

/** The step on offer on a laptop and, when it cannot be taken now, why. */
export interface RegisterClientOffer {
  readonly step: RegisterClientStep;
  readonly blocked: string | null;
}

/**
 * R233: what Teslyn Field offers on a record. `licenceRefuses` is the licence gate's refusal (a read-only laptop makes
 * no changes, decision 292), first because connecting would not help. `inCloud` is whether Teslyn Cloud holds the
 * record, null until known (Teslyn Cloud refuses it anyway, and that refusal has the same words).
 */
export function registerClientOffer(input: {
  readonly roles: readonly string[] | null;
  readonly record: Pick<RegisterRecord, 'deleted_at'>;
  readonly choice: Pick<RegisterClientChoice, 'shown'> | null;
  readonly reach: RegisterCloudReach;
  readonly licenceRefuses: string | null;
  readonly inCloud: boolean | null;
}): RegisterClientOffer | null {
  const step = registerClientStep(input.roles, input.record, input.choice);
  if (!step) return null;
  const why = (): string | null => {
    if (input.licenceRefuses) return input.licenceRefuses;
    if (input.reach === 'offline') return REGISTER_CONNECT_WORDS[step];
    if (input.reach === 'signed_out') return REGISTER_SIGN_IN_WORDS[step];
    if (input.reach === 'unavailable') return REGISTER_CLOUD_QUIET_WORDS;
    if (input.reach === 'looking') return REGISTER_UNKNOWN_LINE.looking;
    return step === 'approve' && input.inCloud === false ? REGISTER_NOT_IN_CLOUD_WORDS : null;
  };
  return { step, blocked: why() };
}

// ------------------------------------------------------------------------------------------------ the details

/**
 * A record's details, label and value, only those filled, in the order shown: the staff page's details and, since
 * decision 1035, the client portal's alike (the staff page adds its "Client" line).
 */
export function registerDetailLines(
  e: RegisterRecord & { readonly project_number: string; readonly project_title: string },
  photos: readonly SafetyPhoto[],
): [string, string][] {
  const b = e.from_breach ?? null;
  const rows: [string, string][] = [
    ['Kind', REGISTER_KIND_WORDS[e.kind] ?? e.kind],
    ['Date and time', [printedDate(e.occurred_on), e.occurred_at].filter(Boolean).join(' ')],
    ['Project', `${e.project_number} ${e.project_title}`.trim()],
    ['Reported by', [e.reported_by_name, e.reported_by_role].filter(Boolean).join(', ')],
    ['People involved', e.people.map((p) => (p.role ? `${p.name} (${p.role})` : p.name)).join(', ')],
    ['Aircraft', e.aircraft_registration],
    ['Location', e.location],
    ['Place on the map', e.lat !== null && e.lon !== null ? `${e.lat.toFixed(5)}, ${e.lon.toFixed(5)}` : ''],
    ['Landholder', [e.landholder_name, e.landholder_details, e.landholder_phone].filter(Boolean).join(', ')],
    ['Description', e.description],
    [safetyActionWords(e.kind), e.actions],
    ['Corrective action has been taken', b ? '' : e.corrective_action_taken ? 'Yes' : 'No'],
    ['Follow-up', e.follow_up],
    ['Follow-up due', printedDate(e.follow_up_due)],
    ['Photos', safetyPhotoNames(e.photo_ids, photos).join('; ')],
    [
      b ? 'Reason recorded' : 'Closed',
      e.closed_at ? [printedDate(e.closed_at), e.closed_by_name].filter(Boolean).join(' by ') : '',
    ],
    [
      'Reason changed',
      b && b.reason_changes > 0
        ? b.reason_changes === 1
          ? 'Once, in the breach report'
          : `${b.reason_changes} times, in the breach report`
        : '',
    ],
    ['Withdrawn from the breach report', b?.withdrawn ? breachWithdrawnText(b.withdrawn) : ''],
    ['Recorded by', e.recorded_by_name],
  ];
  return rows.filter(([, v]) => (v ?? '').trim() !== '');
}

// ---------------------------------------------------------------------------------------------- what the client reads

interface ClientRowBase {
  readonly id: string;
  readonly project_id: string;
  readonly project_number: string;
  readonly project_title: string;
  readonly kind: RegisterKind;
  readonly shown_at: string;
}

/**
 * A record as `my_safety_register_all` (and `my_safety_register`) answer it (20261104238000; decision 1035): the
 * whole record, without the ids that mean something only inside the operator's organisation.
 */
export type ClientRegisterRow =
  | (ClientRowBase & {
      readonly source: 'safety_entry';
      readonly occurred_on: string;
      readonly occurred_at: string;
      readonly topic: string;
      readonly reported_by_name: string;
      readonly reported_by_role: string;
      readonly people: readonly { readonly name: string; readonly role: string }[];
      readonly aircraft_registration: string;
      readonly location: string;
      readonly lat: number | null;
      readonly lon: number | null;
      readonly landholder_name: string;
      readonly landholder_details: string;
      readonly landholder_phone: string;
      readonly description: string;
      readonly actions: string;
      readonly corrective_action_taken: boolean;
      readonly follow_up: string;
      readonly follow_up_due: string | null;
      readonly photos: readonly SafetyPhoto[];
      readonly closed_at: string | null;
      readonly closed_by_name: string;
      readonly recorded_by_name: string;
      readonly created_at: string;
      readonly updated_at: string;
    })
  | (ClientRowBase & {
      readonly source: 'height_breach';
      readonly test_key: HeightBreachKind;
      readonly line_name: string;
      readonly fid_start: number;
      readonly fid_end: number;
      readonly facts: HeightBreachFacts;
      readonly reason: string;
      readonly history: HeightBreachEntry['history'];
      readonly created_at: string;
      readonly created_by_name: string;
      readonly updated_at: string;
    });

/** A record as the client portal's Safety register page shows it: the staff page's record, with when it was shared. */
export interface ClientRegisterRecord extends RegisterRecord {
  readonly source: RegisterRecordSource;
  readonly project_number: string;
  readonly project_title: string;
  readonly shown_at: string;
  /** The linked photos, named (the client reads their display size and thumbnail, never the original). */
  readonly photos: readonly SafetyPhoto[];
}

const list = <T>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** The client's records, newest first, laid out as the staff register lays out its own (decision 1035). */
export function clientRegisterRecords(rows: readonly ClientRegisterRow[]): ClientRegisterRecord[] {
  return rows
    .map((r): ClientRegisterRecord => {
      const shared = {
        source: r.source,
        project_number: r.project_number ?? '',
        project_title: r.project_title ?? '',
        shown_at: r.shown_at,
      };
      if (r.source === 'height_breach') {
        const breach: HeightBreachEntry = {
          id: r.id,
          project_id: r.project_id,
          run_id: '',
          result_id: '',
          flight_id: null,
          test_key: r.test_key,
          line_name: r.line_name,
          fid_start: r.fid_start,
          fid_end: r.fid_end,
          facts: r.facts,
          reason: r.reason ?? '',
          picture_sha256: '',
          history: list(r.history),
          created_at: r.created_at,
          created_by_name: r.created_by_name ?? '',
          updated_at: r.updated_at,
          deleted_at: null,
          deleted_reason: null,
          deleted_by_name: null,
        };
        return { ...registerRecordFromBreach(breach), ...shared, photos: [] };
      }
      const photos = list<SafetyPhoto>(r.photos);
      const entry: SafetyEntry = {
        id: r.id,
        project_id: r.project_id,
        kind: r.kind as SafetyEntry['kind'],
        occurred_on: String(r.occurred_on).slice(0, 10),
        occurred_at: r.occurred_at ?? '',
        topic: r.topic,
        reported_by_person_id: null,
        reported_by_name: r.reported_by_name ?? '',
        reported_by_role: r.reported_by_role ?? '',
        people: list<{ name?: string; role?: string }>(r.people).map((p) => ({
          person_id: null,
          name: p.name ?? '',
          role: p.role ?? '',
        })),
        aircraft_id: null,
        aircraft_registration: r.aircraft_registration ?? '',
        location: r.location ?? '',
        lat: r.lat ?? null,
        lon: r.lon ?? null,
        land_parcel_id: null,
        landholder_name: r.landholder_name ?? '',
        landholder_details: r.landholder_details ?? '',
        landholder_phone: r.landholder_phone ?? '',
        description: r.description ?? '',
        actions: r.actions ?? '',
        corrective_action_taken: r.corrective_action_taken === true,
        follow_up: r.follow_up ?? '',
        follow_up_due: r.follow_up_due ? String(r.follow_up_due).slice(0, 10) : null,
        photo_ids: photos.map((p) => p.id),
        closed_at: r.closed_at ?? null,
        closed_by_name: r.closed_by_name ?? '',
        recorded_by_name: r.recorded_by_name ?? '',
        created_at: r.created_at,
        updated_at: r.updated_at,
        deleted_at: null,
      };
      return { ...entry, from_breach: null, ...shared, photos };
    })
    .sort((a, b) => safetyOrder(a, b) || b.shown_at.localeCompare(a.shown_at));
}
